import { RpcError } from '@protobuf-ts/runtime-rpc'
import { SimpleConfirmationDialog } from 'components/core/confirmation/dialog/SimpleConfirmationDialog'
import { C1Link } from 'components/core/links/C1Link'
import { ClosableProps } from 'components/no-ui/context/modal-provider'
import { useAlert } from 'hooks/useAlert'
import { TaskValue } from 'lib/actions/task'
import { routes } from 'lib/routes'
import { FC, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useTicketActionTracker } from 'redux_toolkit/lib/TicketActionTracker'

export type SkipStepDialogV2Props = {
    task: TaskValue
} & ClosableProps

export const SkipStepTaskDialogV2: FC<SkipStepDialogV2Props> = ({ task, close }) => {
    const intl = useIntl()
    const toast = useAlert()
    const ticketActionTracker = useTicketActionTracker()

    const handleClick = useCallback(async (): Promise<void> => {
        try {
            await ticketActionTracker?.skipStep(task)
        } catch (err: unknown) {
            return toast(
                <FormattedMessage
                    id="task_skip_step_error"
                    defaultMessage="Error skipping step for task {task_link}. {code}: {msg}"
                    values={{
                        task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                        code: (err as RpcError).code,
                        msg: (err as RpcError).message,
                    }}
                />,
                'error',
            )
        }

        toast(
            <FormattedMessage
                id="task_skip_step_success"
                defaultMessage="Step skipped successfully for task {task_link}"
                values={{
                    task_link: <C1Link href={routes.task(String(task.numericId || ''))}>{task.numericId}</C1Link>,
                }}
            />,
            'success',
        )

        close()
    }, [close, task, ticketActionTracker, toast])

    return (
        <SimpleConfirmationDialog
            color="warning"
            submitActionLabel={intl.formatMessage({
                id: 'skip_step_confirmation_dialog_action_string',
                defaultMessage: 'Skip step',
            })}
            title={intl.formatMessage({
                id: 'skip_step_confirmation_dialog_title',
                defaultMessage: 'Are you sure you want to skip this step?',
            })}
            details={intl.formatMessage({
                id: 'skip_step_confirmation_dialog_description',
                defaultMessage:
                    'Skipping this step will immediately move it to the next step in the policy workflow. Policy steps that are skipped are assumed to have completed successfully.',
            })}
            onConfirm={handleClick}
            onCancel={close}
            inProgress={!ticketActionTracker}
            close={close}
        />
    )
}
